
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, ArrowRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSWAssessment } from './SWAssessmentContext';
import { getLatestSWChangeListForVerification, getSWAssessmentMapping } from '../../services/apiService';
import { decodedToken, removeAuthToken } from '../../utils/auth';
import * as XLSX from 'xlsx-js-style';

const SWVerificationReview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [assessmentTestCases, setAssessmentTestCases] = useState(false);
  const [testCasesData, setTestCasesData] = useState([]);
  const [loadingTestCases, setLoadingTestCases] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { getTabData, currentTab } = useSWAssessment();
  const activeTab = location.state?.activeTab || currentTab || 'TV';
  const subType = location.state?.subType || null;
  const activeDataKey =
    location.state?.activeDataKey || subType || activeTab;
  const currentTabData = getTabData(activeDataKey);
  const selectedChanges =
    location.state?.selectedChanges || currentTabData.selectedChanges || {};
  const referenceModels =
    location.state?.referenceModels || currentTabData.referenceModels || {};
  const changeListId =
    location.state?.changeListId || currentTabData.changeListId;
  const changeListData =
    location.state?.changeListData || currentTabData.changeListData;
  const effectiveProductType = subType || activeTab;
  const assessmentItems = ['SW'];
  const [forceUpdate, setForceUpdate] = useState(0);
  const [detailsByGroup, setDetailsByGroup] = useState({});
  const [selectedGroupDetail, setSelectedGroupDetail] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [showNoAssessmentPopup, setShowNoAssessmentPopup] = useState(false);

  useEffect(() => {
    console.log('VerificationReview - Selected changes updated:', selectedChanges);
    console.log('VerificationReview - Effective product type:', effectiveProductType);
    setForceUpdate(prev => prev + 1);
  }, [selectedChanges, effectiveProductType]);

  const handleBackClick = () => {
    navigate('/sw-change', {
      state: {
        activeTab,
        activeDataKey,
        selectedChanges: selectedChanges,
        referenceModels: referenceModels,
        changeListId,
        changeListData,
        subType: subType
      }
    });
  };

  const cleanCategoryName = (name = '') =>
    name
      .replace(/^.*?_\d+\.\d+\s*/, '')
      .replace(/^\d+\.\d+\s*/, '')
      .trim();

  const sortChangeIds = (changeIds) => {
    const grouped = {};
    const order = [];

    changeIds.forEach(id => {
      const match = String(id)
        .trim()
        .match(/^([A-Z]+)(\d+(?:\.\d+)?)$/);
      if (match) {
        const [, prefix, number] = match;
        if (!grouped[prefix]) {
          grouped[prefix] = [];
          order.push(prefix);
        }
        grouped[prefix].push({
          id: String(id).trim(),
          parts: number
            .split('.')
            .map(n => parseInt(n, 10))
        });
      }
    });

    const result = [];
    order.forEach(prefix => {
      const sorted = grouped[prefix].sort((a, b) => {
        const maxLen = Math.max(
          a.parts.length,
          b.parts.length
        );

        for (let i = 0; i < maxLen; i++) {

          const av = a.parts[i] || 0;
          const bv = b.parts[i] || 0;

          if (av !== bv) {
            return av - bv;
          }
        }

        return 0;
      });

      result.push(
        ...sorted.map(item => item.id)
      );
    });

    return result;
  };

  const mergeDuplicateTestCases = (testCases) => {
    const mergedMap = new Map();

    testCases.forEach(testCase => {
      const key = `${testCase.testCategory}|||${testCase.testItem}`;

      if (mergedMap.has(key)) {
        const existing = mergedMap.get(key);
        const combinedChangeIds = [...existing.changeIds];
        testCase.changeIds.forEach(id => {
          if (!combinedChangeIds.includes(id)) {
            combinedChangeIds.push(id);
          }
        });

        mergedMap.set(key, {
          ...existing,
          changeIds: sortChangeIds(combinedChangeIds),
        });
      } else {
        mergedMap.set(key, {
          ...testCase,
          changeIds: sortChangeIds([...testCase.changeIds])
        });
      }
    });

    const mergedArray = Array.from(mergedMap.values());
    return mergedArray.map((item, index) => ({
      ...item,
      no: index + 1
    }));
  };


     const handleAssessmentTestCases = async () => {
     try {
       setLoadingTestCases(true);
       setErrorMessage('');

       const tokenData = decodedToken();
       const userEmail = tokenData?.email || '';
       const userType = tokenData?.userType || '';

       const selectedAppliedChanges = Object.values(selectedChanges)
         .flat()
         .filter(change => change.applied)
         .map(change => change.id);

      const isCombinedSource =
        currentTabData?.dataSource === 'sw-import-combined' ||
        changeListId === 'combined';

      const changeListIdForMapping = isCombinedSource
        ? null
        : changeListId;

       const response = await getSWAssessmentMapping(
         effectiveProductType,
         userEmail,
         userType,

        changeListIdForMapping,
         selectedAppliedChanges
       );
       setAssessmentData(response);

      if (response?.noAssessmentFile) {
        setShowNoAssessmentPopup(true);
        setTestCasesData([]);
        setAssessmentTestCases(false);
        return;
      }

      const assessmentRows = response?.assessmentRows || [];
      const testSheets = response?.testSheets || {};
      const apiCategoryNames = response?.testCategoryNames || {};

      const PRIORITY = { MUST: 3, 'NICE-TO': 2, 'NO NEED': 1 };
      const higher = (a, b) =>
        (PRIORITY[a] || 0) >= (PRIORITY[b] || 0) ? a : b;

      const extractGroupNum = (name) => {
        const m = name.match(/_(\d+)\./) || name.match(/^(\d+)\./);
        return m ? m[1] : null;
      };

      // ================= GROUP MAP =================
      const GROUP_MAP = {};
      Object.entries(apiCategoryNames).forEach(([g, info]) => {
        if (info?.label) GROUP_MAP[g] = info.label;
      });

      if (Object.keys(GROUP_MAP).length === 0) {
        Object.keys(testSheets).forEach(sheetName => {
          const g = extractGroupNum(sheetName);
          if (g) GROUP_MAP[g] = sheetName;
        });
      }

      // ================= SUMMARY =================
      const groupSummary = {};

      Object.entries(testSheets).forEach(([sheetName, sheetData]) => {
        const groupNum = extractGroupNum(sheetName);
        if (!groupNum) return;

        if (!groupSummary[groupNum]) {
          groupSummary[groupNum] = {
            isRequired: null,
            testingOwner: null,
            leadTimes: [],
            affectedCodes: []
          };
        }

        const testCategoryKey = sheetData.testCategory || sheetName;

        Object.keys(sheetData.mappings || {}).forEach(code => {
          const relatedRows = assessmentRows.filter(r =>
            r.changeItemId === code || r.changeItemId.startsWith(code)
          );

          relatedRows.forEach(row => {
            const testResult = row.testResults?.[testCategoryKey];

            if (testResult) {

              if (testResult.isRequired) {
                groupSummary[groupNum].isRequired = higher(
                  groupSummary[groupNum].isRequired,
                  testResult.isRequired
                );
              }

              if (testResult.testingOwner && !groupSummary[groupNum].testingOwner) {
                groupSummary[groupNum].testingOwner = testResult.testingOwner;
              }

              if (testResult.leadTime) {
                  const leadTime = String(testResult.leadTime).trim();

                if (leadTime && !groupSummary[groupNum].leadTimes.some( item => item.changeItemId === code)
                ) { groupSummary[groupNum].leadTimes.push({
                    changeItemId: code,
                    leadTime
                    });
                  }}
            }
          });

          groupSummary[groupNum].affectedCodes.push(code);
        });
      });

      const summaryRows = Object.entries(GROUP_MAP)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([groupNum, groupName], idx) => {
          const gs = groupSummary[groupNum] || {};
          return {
            no: idx + 1,
            groupNum,
            testCategory: groupName,
            isRequired: gs.isRequired || '-',
            leadTime: gs.leadTimes?.length
            ? gs.leadTimes
            .map(item => `${item.changeItemId}: ${item.leadTime}`)
            .join(', ')
            : '-',
            testingOwner: gs.testingOwner || '-',
            affectedCodes: gs.affectedCodes || [],
            leadTimeDetails: gs.leadTimes || []
            };
        });

      // ================= DETAILS =================
      const detailsMap = {};
      const selectedSet = new Set(selectedAppliedChanges);

      Object.entries(testSheets).forEach(([sheetName, sheetData]) => {
        const groupNum = extractGroupNum(sheetName);
        if (!groupNum) return;

        if (!detailsMap[groupNum]) {
          detailsMap[groupNum] = {
            testCategory: sheetData.testCategory || sheetName,
            pillars: [...(sheetData.pillars || [])],
            rows: []
          };
        }

        const testCategoryKey = sheetData.testCategory || sheetName;

        Object.entries(sheetData.mappings || {}).forEach(([code, mapping]) => {
          if (!mapping) return;

          const isMatched = Array.from(selectedSet).some(
            sel =>
              String(sel).trim() ===
              String(code).trim()

          );
          if (!isMatched) return;

          let pillarValues = {};
          let leadTime = '';
          let testingOwner = '';
          let isRequired = '';

          const relatedRows = assessmentRows.filter(
            r =>
              String(r.changeItemId).trim() ===
              String(code).trim()
          );

          relatedRows.forEach(row => {
            const testResult = row.testResults?.[testCategoryKey];

            if (testResult) {
            isRequired = testResult.isRequired || '';
            testingOwner = testResult.testingOwner || '';
            leadTime = testResult.leadTime || '';

              pillarValues = {
                ...pillarValues,
                isRequired,
                testingOwner,
                leadTime
              };
            }
          });
          const summary = mapping?.summary || {};
          const isReqKey = Object.keys(summary).find(k =>
            k.toLowerCase().includes('is sw testing')
          );

          const requiredFromSummary = isReqKey ? summary[isReqKey] : '';

          detailsMap[groupNum].rows.push({
            changeItemId: code,
            pillarValues: mapping?.pillars || {},
            required: requiredFromSummary || '',
            leadTime,
            testingOwner,
            isRequired
          });

        });
      });

      setTestCasesData(summaryRows);
      setDetailsByGroup(detailsMap);
      setAssessmentTestCases(true);

    } catch (error) {
      console.error(error);
      setErrorMessage('Failed to load test cases');
    } finally {
      setLoadingTestCases(false);
    }
  };


  const handleCertificationClick = (testItem) => {
    const changeIdsWithCert = [];

    testCasesData.forEach(row => {
      if (row.qty === 'Y') {
        changeIdsWithCert.push(...row.changeIds);
      }
    });

    const uniqueChangeIds = [...new Set(changeIdsWithCert)];

    navigate('/certification-overview', {
      state: {
        selectedChangeIds: uniqueChangeIds,
        productType: effectiveProductType
      }
    });
  };

  const sanitizeSheetName = (name = '') => {
    return name
      .replace(/[:\\/?*\[\]]/g, '')
      .substring(0, 31)
      .trim() || 'Sheet';
  };

  const handleExportToExcel = () => {
  const workbook = XLSX.utils.book_new();

  const cleanCategoryName = (name = '') =>
    name
      .replace(/^.*?_\d+\.\d+\s*/, '')
      .replace(/^\d+\.\d+\s*/, '')
      .trim();

  const sanitizeSheetName = (name = '') => {
    return name
      .replace(/[:\\/?*\[\]]/g, '')
      .substring(0, 31)
      .trim() || 'Sheet';
  };

  const applyHeaderStyle = (sheet) => {
    if (!sheet['!ref']) return;

    const range = XLSX.utils.decode_range(sheet['!ref']);

    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });

        if (!sheet[cellAddress]) continue;

        sheet[cellAddress].s = {
          alignment: {
            horizontal: 'center',
            vertical: 'center',
          },
        };

        if (R === 0) {
          sheet[cellAddress].s = {
            ...sheet[cellAddress].s,
            fill: { fgColor: { rgb: '4EA3C3' } },
            font: { bold: true, color: { rgb: 'FFFFFF' } },
          };
        }
      }
    }
  };

  const summaryData = testCasesData.map(item => ({
    No: item.no,
    'Test Category': cleanCategoryName(item.testCategory),
    'Is SW testing required': item.isRequired,
    'SW testing Lead time (Unit: Hours)': item.leadTime,
    'More Detail': '→',
    'Testing owner': item.testingOwner,
  }));

  const summarySheet = XLSX.utils.json_to_sheet(summaryData);
  applyHeaderStyle(summarySheet);
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  const categoryMap = {};
  testCasesData.forEach(item => {
    if (!categoryMap[item.groupNum]) {
      categoryMap[item.groupNum] = {
        ...item,
        cleanName: cleanCategoryName(item.testCategory),
      };
    }
  });

Object.values(categoryMap).forEach(category => {
  const cleanName = category.cleanName;
  const sheetRows = [];
  const groupData = detailsByGroup?.[category.groupNum];

  // Get ONLY the current UI data for this category
  const currentCategoryItems = testCasesData.filter(
    item => item.groupNum === category.groupNum
  );
  if (groupData?.rows?.length && groupData?.pillars?.length) {
    groupData.rows.forEach(row => {
      groupData.pillars.forEach(pillar => {
        const required = Object.prototype.hasOwnProperty.call(
          row.pillarValues || {},
          pillar
        )
          ? row.pillarValues[pillar] ?? ''
          : '';

        sheetRows.push({
          No: sheetRows.length + 1,
          'Test Category': cleanName,
          'Test Item': pillar.replace(/^\d+\_/, ''),
          'Is SW testing required': required,
          'SW testing Lead time (test to completed)':
             row.leadTime || '',
          'Testing owner':
             row.testingOwner || '',
          'Corresponding Change Item ID':
            row.changeItemId || '',
        });
      });
    });
  }

  else {
    currentCategoryItems.forEach(item => {
      const testItem = String(item.testItem || '').trim();

      // If UI has no Test Item, don't create a fake row
      if (!testItem) {
        return;
      }

      sheetRows.push({
        No: sheetRows.length + 1,
        'Test Category': cleanName,
        'Test Item': testItem,
        'Is SW testing required': item.isRequired || '',
        'SW testing Lead time (test to completed)':
          item.leadTime || '',
        'Testing owner':
          item.testingOwner || '',
        'Corresponding Change Item ID':
          item.changeItemId || '',
      });
    });
  }

  const sheetData =
    sheetRows.length > 0
      ? sheetRows
      : [
          {
            No: '',
            'Test Category': cleanName,
            'Test Item': '',
            'Is SW testing required': '',
            'SW testing Lead time (test to completed)': '',
            'Testing owner': '',
            'Corresponding Change Item ID': '',
          },
        ];

  const sheet = XLSX.utils.json_to_sheet(sheetData);
  applyHeaderStyle(sheet);

  XLSX.utils.book_append_sheet(
    workbook,
    sheet,
    sanitizeSheetName(cleanName)
  );
});

  XLSX.writeFile(workbook, `SW_Assessment_${effectiveProductType}.xlsx`);
};

  const handleBackFromTestCases = () => {
    setAssessmentTestCases(false);
  };

  if (selectedGroupDetail) {
    const groupNum = selectedGroupDetail.groupNum;
    const groupData = detailsByGroup[groupNum] || { pillars: [], rows: [] };

    const pillars = groupData.pillars || [];
    const rows = groupData.rows || [];


    const flatRows = [];

  rows.forEach(row => {
  pillars.forEach(pillar => {

    // Only use the actual pillar value
        let required = '';

    // Check whether this pillar exists in the response
        if (
          row.pillarValues &&
          Object.prototype.hasOwnProperty.call(
            row.pillarValues,
            pillar
          )
        ) {
          required = row.pillarValues[pillar] || '';
    }

    if (row.changeItemId === 'A1.2') {
  console.log(
    'UI RENDER',
    {
      changeId: row.changeItemId,
      pillar,
      allPillars: row.pillarValues,
      currentValue: row.pillarValues?.[pillar]
    }
  );
}

        flatRows.push({
          testCategory: selectedGroupDetail.groupName,
          testItem: pillar,
          changeItemId: row.changeItemId,
          required
        });

      });
    });

    return (

      <div className="min-h-screen bg-black text-white">
        {/* Page Header */}
        <div className="sticky top-0 z-30 bg-black border-b border-gray-600">
          <div className="flex items-center px-6 py-4">
            <button
              onClick={() => setSelectedGroupDetail(null)}
              className="w-10 h-10 hover:bg-gray-800 rounded"
            >
              <ArrowLeft className="w-8 h-5" />
            </button>
            <h1 className="text-lg font-semibold ml-4">
              SW Change Verification Assessment / More Details
            </h1>
          </div>
        </div>

        <div className="px-6 py-6 max-w-6xl mx-auto">

          {/* Sticky Title */}
          <div className="bg-gray-700 text-center py-2 font-semibold sticky top-[64px] z-20">
            {cleanCategoryName(selectedGroupDetail.groupName)}
          </div>
          {/* Table */}
          <div className="border border-gray-600 max-h-[90vh] overflow-auto">
            <div className="min-w-[700px]">

              {/* Header */}
              <div className="grid 
                grid-cols-[50px_150px_minmax(200px,1fr)_200px_100px] 
                md:grid-cols-[60px_200px_1fr_250px_120px]
                bg-gray-800 text-base font-semibold 
                sticky top-0 z-20 border-b border-gray-600">

                <div className="sm:p-3 whitespace-nowrap">No.</div>
                <div className="sm:p-3 whitespace-nowrap">Test Category</div>
                <div className="sm:p-3">Test Item</div>
                <div className="sm:p-3  sm:whitespace-nowrap">Corresponding Change Item ID</div>
                <div className="sm:p-3 whitespace-nowrap">Required</div>
              </div>
              {flatRows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="text-gray-500 text-6xl mb-4">📋</div>

                  <h3 className="text-xl font-semibold mb-2 text-gray-400">
                    No Data Available
                  </h3>

                  <p className="text-gray-500 text-center">
                    No assessment items found for this section.
                  </p>
                </div>
              ) : (
                flatRows.map((row, idx) => {
                  const requiredValue = row.required || '';

                  return (
                    <div
                      key={idx}
                      className="grid 
                      grid-cols-[50px_150px_minmax(200px,1fr)_200px_100px] 
                      md:grid-cols-[60px_200px_1fr_250px_120px]
                      border-t border-gray-700 text-sm"
                    >
                      <div className="p-3">{idx + 1}</div>

                      <div className="p-3 break-words">
                        {cleanCategoryName(row.testCategory)}
                      </div>

                      <div className="p-3 break-words">
                        {row.testItem}
                      </div>

                      <div className="p-3 text-blue-400 break-words">
                        {row.changeItemId}
                      </div>

                      <div
                        className={`p-3 font-semibold ${requiredValue === 'MUST'
                            ? 'text-blue-400'
                            : requiredValue === 'NICE-TO'
                              ? 'text-yellow-400'
                              : requiredValue === 'NO NEED'
                                ? 'text-gray-400'
                                : 'text-gray-500'
                          }`}
                      >
                        {requiredValue}
                      </div>
                    </div>
                  );
                })
              )}

            </div>
          </div>
        </div>
      </div>
    );
  }

  if (assessmentTestCases) {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="sticky top-0 z-20 bg-black/95 backdrop-blur-sm border-b border-gray-600">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center sm:ml-8">
              <button
                onClick={handleBackFromTestCases}
                className="flex items-center justify-center w-12 h-12 sm:-ml-5 -ml-0 rounded-lg hover:bg-gray-800 active:bg-gray-700 transition-colors duration-200 touch-manipulation"
                aria-label="Go back"
              >
                <ArrowLeft className="w-6 h-6 text-white sm:-ml-5 -ml-0" />
              </button>
              <h1 className="sm:text-2xl text-lg font-semibold text-white">
                Assessment Test Cases
              </h1>
            </div>
          </div>
        </div>

        <div className="px-6 py-6 max-w-7xl mx-auto sm:ml-15">
          <div className="mb-6">
            <p className="text-gray-400 text-base">Recommend Verification Test Items</p>
          </div>

          {loadingTestCases ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin  h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-gray-400">Loading test cases from template...</p>
              </div>
            </div>
          ) : testCasesData.length === 0 ? (

            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-gray-500 text-6xl mb-4">📋</div>
              <h3 className="text-xl font-semibold mb-2 white">No Test Cases Found</h3>
              <p className="text-gray-500 text-center max-w-md">
                No test cases were generated. This may happen if no test cases are present
                for the selected content detail, or the template does not contain matching entries.
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto md:overflow-visible border border-gray-700">

                {/* Header */}
                <div className="grid 
                  grid-cols-[auto_1fr_1fr_1fr_1fr_100px] 
                  md:grid-cols-[auto_2fr_2fr_2fr_1.5fr_120px]
                  min-w-[800px] md:min-w-0
                  bg-gray-700/90 border-b border-gray-600 text-white font-semibold text-sm md:text-base">

                  <div className="px-3 py-2 whitespace-nowrap">No.</div>
                  <div className="px-3 py-2">Test Category</div>
                  <div className="px-3 py-2 text-center">Is SW testing required</div>
                  <div className="px-3 py-2 text-center">SW testing Lead time</div>
                  <div className="px-3 py-2 text-center">Testing owner</div>
                  <div className="px-3 py-2 text-center whitespace-nowrap">More Detail</div>
                </div>

                {/* Rows */}
                {testCasesData.map((row, index) => (
                  <div
                    key={index}
                    className={`grid 
                    grid-cols-[auto_1fr_1fr_1fr_1fr_100px] 
                    md:grid-cols-[auto_2fr_2fr_2fr_1.5fr_120px]
                    min-w-[800px] md:min-w-0
                    border-b border-gray-700/60 
                    text-sm md:text-base hover:bg-gray-800/30 transition-colors
                    ${index % 2 === 0 ? 'bg-gray-900/20' : 'bg-gray-900/40'}`}
                  >
                    <div className="px-3 py-3 whitespace-nowrap">{row.no}</div>

                    <div className="px-3 py-3 break-words">
                      {/* {row.testCategory} */}
                      {cleanCategoryName(row.testCategory)}
                    </div>

                    <div className="px-3 py-3 text-center">
                      <span className={`font-semibold ${row.isRequired === 'MUST'
                        ? 'text-blue-400'
                        : row.isRequired === 'NICE-TO'
                          ? 'text-yellow-400'
                          : 'text-gray-500'
                        }`}>
                        {row.isRequired}
                      </span>
                    </div>

                    <div className="px-3 py-3 text-center">
                      {row.leadTime}
                    </div>

                    <div className="px-3 py-3 text-center">
                      {row.testingOwner}
                    </div>

                    <div className="px-3 py-3 flex justify-center">
                      <button
                        onClick={() =>
                          setSelectedGroupDetail({
                            groupNum: row.groupNum,
                            groupName: row.testCategory,
                          })
                        }
                        className="text-white hover:text-gray-300 transition-colors"
                      >
                        <ArrowRight className="w-6 h-4 scale-x-125" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="flex justify-center md:justify-end sm:mt-6 md-mt-2 md:mr-24 lg:mr-200 xl:mr-120">
          <button
            onClick={handleExportToExcel}
            disabled={testCasesData.length === 0}
            className="flex items-center gap-2 px-5 py-2.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Export to Excel
          </button>
        </div>
      </div>
    );
  }

  const hasAnySelectedChanges = assessmentItems.some(item =>
    selectedChanges[item] && selectedChanges[item].length > 0
  );

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="sticky top-0 z-20 bg-black/95 backdrop-blur-sm ">
        <div className="px-4 py-3 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-1">
            <h1 className="pt-4 text-base font-medium sm:text-3xl text-white mb-2 sm:mb-0 text-center sm:text-left">
              Verification Cases Review - {effectiveProductType}
            </h1>
            <div className="flex flex-col sm:items-end space-y-2">
              <button
                onClick={handleBackClick}

                className="px-6 sm:px-25  sm:py-1 bg-gray-600 hover:bg-gray-700 active:bg-gray-800 
             rounded-full text-white font-medium transition-all duration-200 
             text-sm sm:text-base min-h-[36px] sm:min-h-[40px] shadow-lg hover:shadow-xl"
              >
                Back
              </button>

              <button
                onClick={handleAssessmentTestCases}

                className="px-4 sm:px-9  sm:py-1 bg-blue-600 hover:bg-blue-700 active:bg-gray-800 
             rounded-full text-white font-medium transition-all duration-200 
             text-sm sm:text-base min-h-[36px] sm:min-h-[40px] shadow-lg hover:shadow-xl"
              >
                Assessment Test Cases
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="justify-left px-4 py-6 sm:px-6 max-w-6xl" key={forceUpdate}>
        <div className="flex justify-between items-start">
          {/* Left side — Table section */}
          <div className="flex-1">
            {assessmentItems.map((item) => {
              const changes = selectedChanges[item] || [];
              const appliedChanges = changes.filter(change => change.applied);

              if (appliedChanges.length === 0) {
                return (
                  <div key={item} className="mb-8">
                    <div className="bg-gray-800/30 rounded-t-lg px-4 py-3 border border-gray-700">
                      <h2 className="text-lg font-semibold text-white">
                        {item} Change Evaluation
                      </h2>
                    </div>

                    <div className="bg-gray-900/20 border-l border-r border-b border-gray-700 rounded-b-lg">
                      <div className="flex flex-col items-center justify-center h-full px-4 sm:px-6 py-16">
                        <div className="text-gray-500 text-6xl mb-4">📋</div>

                        <h3 className="text-xl font-semibold mb-2 text-gray-400">
                          No Data Available
                        </h3>

                        <p className="text-gray-500 text-center">
                          No assessment items found for {item} section.
                        </p>
                      </div>
                    </div>
                  </div>
                );
              }

              const referenceModel = referenceModels[item] || 'N/A';

              return (
                <div key={item} className="mb-8">
                  <div className="bg-gray-800/30 rounded-t-lg px-4 py-3 border border-gray-700">
                    <h2 className="text-lg font-semibold text-white">
                      {`${item} Change Evaluation Against Reference Model : ${referenceModel}`}
                    </h2>
                  </div>

                  <div className="bg-gray-900/20 border-l border-r border-b border-gray-700 rounded-b-lg">
                    <div className="grid grid-cols-2 bg-gray-800/50 border-b border-gray-700">
                      <div className="px-4 py-3 text-sm font-medium text-gray-300 border-r border-gray-700">
                        Change Item ID
                      </div>
                      <div className="px-4 py-3 text-sm font-medium text-gray-300">
                        Change Content Detail
                      </div>
                    </div>

                    {appliedChanges.map((change, changeIndex) => (
                      <div
                        key={`${item}-${change.id}-${changeIndex}`}
                        className="grid grid-cols-2 hover:bg-gray-800/30 transition-colors duration-200 border-b border-gray-700/50 last:border-b-0"
                      >
                        <div className="px-4 py-3 text-sm text-blue-400 font-medium border-r border-gray-700/50">
                          {change.id}
                        </div>
                        <div className="px-4 py-3 text-sm text-white leading-relaxed">
                          {change.detail}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {!hasAnySelectedChanges && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-gray-500 text-6xl mb-4">📋</div>
            <h3 className="text-xl font-semibold mb-2 text-gray-400">No Changes Selected</h3>
            <p className="text-gray-500 text-center max-w-md">
              No changes have been selected from any assessment sections.
              Please go back and select changes to review.
            </p>
          </div>
        )}

        {showNoAssessmentPopup && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">

            <div className="w-full max-w-md rounded-2xl bg-gray-900 border border-red-700 shadow-2xl animate-fadeIn">

              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-red-700">
                <h2 className="text-lg font-semibold text-white">
                  Assessment File Not Found
                </h2>

                <button
                  onClick={() => setShowNoAssessmentPopup(false)}
                  className="text-gray-400 hover:text-white text-xl"
                >
                  ✕
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-8 flex flex-col items-center text-center">

                <div className="w-20 h-20 rounded-full bg-red-900 flex items-center justify-center mb-5">
                  <span className="text-5xl">📋</span>
                </div>

                <h3 className="text-xl font-semibold text-white mb-3">
                  No Assessment Data File Found
                </h3>

                <p className="text-gray-400 leading-relaxed">
                  For selected item changes there is no assessment
                  file found.
                </p>
                 <p className="text-gray-400 leading-relaxed">
                  Please upload the required SW Change Assessment Reference Table file in Template Management.
                </p>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-red-700 flex justify-center">
                <button
                  onClick={() => setShowNoAssessmentPopup(false)}
                  className="px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SWVerificationReview;